import * as React from "react"
import { cn } from "@/lib/utils"

const InputGroup = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "flex flex-col rounded-2xl border border-input bg-background transition-colors focus-within:border-ring/50",
        className,
      )}
      {...props}
    />
  ),
)
InputGroup.displayName = "InputGroup"

interface InputGroupAddonProps extends React.HTMLAttributes<HTMLDivElement> {
  align?: "block-start" | "block-end"
}

const InputGroupAddon = React.forwardRef<HTMLDivElement, InputGroupAddonProps>(
  ({ className, align = "block-end", ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "flex items-center gap-1 px-2 py-2",
        align === "block-start" && "border-b",
        align === "block-end" && "border-t",
        className,
      )}
      {...props}
    />
  ),
)
InputGroupAddon.displayName = "InputGroupAddon"

interface InputGroupButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  size?: "icon-sm" | "icon" | "sm"
  variant?: "default" | "outline" | "ghost"
}

const inputGroupButtonVariants: Record<string, string> = {
  "icon-sm": "size-8 rounded-xl",
  icon: "size-10 rounded-xl",
  sm: "h-8 px-3 rounded-xl text-xs",
}

const inputGroupButtonColorVariants: Record<string, string> = {
  default: "bg-primary text-primary-foreground hover:bg-primary/90",
  outline: "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
  ghost: "hover:bg-accent hover:text-accent-foreground text-muted-foreground",
}

const InputGroupButton = React.forwardRef<HTMLButtonElement, InputGroupButtonProps>(
  ({ className, size = "icon-sm", variant = "ghost", ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      className={cn(
        "inline-flex items-center justify-center gap-2 font-semibold transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-45 [&_svg]:size-4 [&_svg]:shrink-0",
        inputGroupButtonVariants[size],
        inputGroupButtonColorVariants[variant],
        className,
      )}
      {...props}
    />
  ),
)
InputGroupButton.displayName = "InputGroupButton"

export { InputGroup, InputGroupAddon, InputGroupButton }
